/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DownloadIcon, SearchIcon } from '../../../../assets/icons';
import { Constants, fromStorage, getUpdatedState } from '../../../../utils';
import { Input, Pagination, Render } from '../../../../components';
import { ReportService } from '../../../../services';

const Incidents = () => {
  const { data, downloadAll, downloadItem, filters, goToPage, onChange, pagination, reload, search } = useFetchTable();

  return (
    <main className="w-full">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          className={{ base: 'max-w-64' }}
          placeholder="Buscar N° de reporte"
          value={filters.search}
          onValueChange={v => onChange('search', v)}
          prepend={
            <img src={SearchIcon} className="w-4 h-4" alt="" />
          }
        />

        <Input
          className={{ base: 'max-w-44' }}
          placeholder="Placa del vehículo"
          value={filters.plaque}
          onValueChange={v => onChange('plaque', v)}
        />

        <Input
          className={{ base: 'max-w-44' }}
          type="date"
          label="Desde"
          value={filters.since}
          onValueChange={v => onChange('since', v)}
        />

        <Input
          className={{ base: 'max-w-44' }}
          type="date"
          label="Hasta"
          value={filters.until}
          onValueChange={v => onChange('until', v)}
        />

        <div className="flex gap-4">
          <button
            className="bg-red hover:bg-red-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
            onClick={search}
          >
            Filtrar
          </button>
          <button
            className="bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
            onClick={() => reload()}
          >
            Limpiar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden mt-6">
        <div className="flex items-center justify-between py-4 px-3">
          <p className="text-2xl font-bold text-center">
            <span>Historial de Incidencias</span> 
            <Render condition={!filters.since && !filters.until}>
              <span className="capitalize text-base ml-4 px-3 py-1 rounded-full bg-orange/15 border border-red text-red">
                { moment().format('MMMM') }
              </span>
            </Render>
          </p>

          <button
            className="flex items-center gap-2 bg-black text-white px-4 py-1.5 rounded-lg font-semibold"
            onClick={() => toast.promise(downloadAll(), { loading: 'Descargando...', success: 'Reporte descargado con éxito', error: 'Error al descargar el reporte' })}
          >
            <img src={DownloadIcon} className="w-4 h-4 brightness-200" alt="" />
            Descargar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="text-left border-collapse w-full">
            <thead>
              <tr className="border-b border-gray5 [&>th]:px-1 [&>th]:py-2 [&>th]:break-words [&>th]:max-w-36 text-sm text-default font-semibold align-bottom">
                <th className="pl-4">Placa vehículo</th>
                <th>Nº de Reporte</th>
                <th>Fecha/hora de viaje</th>
                <th>Conductor</th>
                <th>Incumplimiento de ruta</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody className="text-sm text-text">
              {data.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray5 transition-colors odd:bg-gray5 [&>td]:py-3 [&>td]:px-1 [&>td]:truncate [&>td]:max-w-36">
                  <td className="text-orange font-bold text-base">
                    <Render condition={!index || item.vehicle_id !== data.at(index - 1)?.vehicle_id}>
                      <span className="pl-4">{ item?.vehicle?.plaque }</span>
                    </Render>
                  </td>
                  <td>{ String(item?.id).padStart(4, '0') }</td>
                  <td>{ item?.order?.date ? moment(item?.order?.date).format('DD/MM/YYYY h:mm a') : '--' }</td>
                  <td>
                    <span className="font-semibold">{ item?.driver?.person?.fullName || '--' }</span>
                  </td>
                  <td>{ item?.incident_type?.name || '--' }</td>
                  <td>
                    <img
                      src={DownloadIcon}
                      className="w-5 h-5 object-contain cursor-pointer"
                      onClick={() => {
                        toast.promise(downloadItem(item.id), {
                          loading: 'Descargando...',
                          success: 'Reporte descargado con éxito',
                          error: 'Error al descargar el reporte',
                        });
                      }}
                      alt=""
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!data.length && (
            <p className="py-8 text-text text-center">Sin datos para mostrar</p>
          )}
        </div>

        <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Pagination
            active={pagination.page}
            pages={pagination.pages}
            onChange={goToPage}
          />
        </div>
      </div>
    </main>
  );
};

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    plaque: '',
    since: '',
    until: '',
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const payload = await getUpdatedState(setFilters);
      const response = await ReportService.owner.incidents.findAll(payload);
      const { data, ...rest } = response;

      setData(data);
      setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      setCanFetch(true);
    }
  }

  const downloadAll = async () => {
    try {
      const response = await ReportService.owner.incidents.downloadAll(filters);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      console.log(error);
    }
  }

  const downloadItem = async (id) => {
    try {
      const response = await ReportService.owner.incidents.downloadOne(id);
      if (response.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      toast.error(String(error));
    }
  }

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    fetchData();
  }

  const goToPage = async (page) => {
    const pagination = await getUpdatedState(setPagination);
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
      fetchData();
    }
  }

  const onChange = (target, value) => {
    setFilters(s => ({ ...s, page: 1, [target]: value }));
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    downloadAll,
    downloadItem,
    filters,
    goToPage,
    isLoading: !canFetch,
    onChange,
    pagination,
    reload,
    search: fetchData,
  }
}

export default Incidents;